// src/pages/SalesRecord/components/SalesRecordTable.js
import React from "react";
import { Table, Button, Tooltip, Popconfirm } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { formatToCompanyTimezone, numberFormatTax } from "../../../../../lib/functions";
import { deleteService } from "../ServiceRecordThunk";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useIsAdminUser } from "../../../../../lib/accessList";
import { useTxLock } from "../../../../../hooks/useTxLock";
import VoucherPdfActions from "../../../../../components/common/VoucherPdfActions";

const ServiceRecordTable = ({ printMode = false }) => {
  // const [isAdminUser, isAdminUserSet] = useState(false);
  const authInfo = useSelector((state) => state.auth.auth_info);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGstColumns = isIndia && isGstEnabled;
  const showTax = !isIndia || isGstEnabled;
  const taxLabel = isIndia ? "GST" : "VAT";
  // useEffect(() => {
  //   const hrRoles = [
  //     // Admin role
  //     "ForthERP_Basic_admin",
  //     "ForthERP_Standard_admin",
  //     "ForthERP_Pro_admin",
  //     "PharmaERP_Basic_admin",
  //     "PharmaERP_Standard_admin",
  //     "PharmaERP_Pro_admin",
  //     "PharmaBooks_Basic_admin",
  //     "PharmaBooks_Standard_admin",
  //     "PharmaBooks_Pro_admin",
  //     "ForthBooks_Basic_admin",
  //     "ForthBooks_Standard_admin",
  //     "ForthBooks_Pro_admin",
  //     "HRPayroll_Standard_admin",
  //     "HRPayroll_Pro_admin",
  //     "SuperERP_admin",
  //     "SuperERP_super_admin",
  //   ];

  //   const userRoles = authInfo.userInfo.user_role_names
  //     ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
  //     : [];

  //   const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

  //   isAdminUserSet(isAdminHR);
  // }, []);
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const { list, loading, filters } = useSelector(
    (state) => state.serviceRecord,
  );
  const dispatch = useDispatch();

  const handleDelete = async (record) => {
    const result = await dispatch(deleteService(record.id));
    if (deleteService.rejected.match(result) && typeof result.payload === "string") {
      Swal.fire(result.payload, "", "warning");
    }
  };

  const navigate = useNavigate();
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const money = (v) => Number(v || 0).toFixed(2);
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Customer / Debtor", dataIndex: "customer" },
    // { title: "Employee", dataIndex: "employee" },
    // { title: "Mode of Pay", dataIndex: "mode" },
    { title: "Created By", dataIndex: "createdBy" },
    {
      title: "Sub Total",
      dataIndex: "subtotal",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      render: (val) => Number(val).toFixed(2),
    },
    showTax && {
      title: taxLabel,
      dataIndex: "vat",
      render: (val) => Number(val).toFixed(2),
    },
    showGstColumns && {
      title: "CGST",
      dataIndex: "cgst_amount",
      render: numberFormatTax,
      align: "right",
    },

    showGstColumns && {
      title: "SGST",
      dataIndex: "sgst_amount",
      render: numberFormatTax,
      align: "right",
    },

    showGstColumns && {
      title: "IGST",
      dataIndex: "igst_amount",
      render: numberFormatTax,
      align: "right",
    },
    {
      title: "Service",
      dataIndex: "service",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Paid",
      dataIndex: "paid",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Due",
      dataIndex: "due",
      render: (val) => Number(val).toFixed(2),
    },
!printMode && {
  title: "Actions",
  className: "no-print",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.date);

    return (
      <>
        <Tooltip title="View Quick Voucher">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/service/quick-service-voucher/${record.id}`);
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            icon={<EditOutlined />}
            disabled={!(isAdminUser || canEdit) || txLocked}
            onClick={(e) => {
              e.stopPropagation();

              if (txLocked) return;

              navigate(`/service/service-update/${record.id}`);
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete Service"
          }
        >
          <Popconfirm
            title="Are you sure you want to delete this service?"
            okText="Yes, Delete"
            disabled={!(isAdminUser || canDelete) || txLocked}
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Button
              type="link"
              disabled={!(isAdminUser || canDelete) || txLocked}
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Tooltip>

        <VoucherPdfActions
          url={`/print/service/${record.id}`}
          fileName={`Service_${record.vch_no || record.id}.pdf`}
        />
      </>
    );
  },
},
  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Customer / Debtor", dataIndex: "customer" },
    // { title: "Employee", dataIndex: "employee" },
    { title: "Created By", dataIndex: "createdBy" },
    // { title: "Mode of Pay", dataIndex: "mode" },
    { title: "Item", dataIndex: "item" },
    { title: "Rate", dataIndex: "rate" },
    // Only show Discount column if at least one row has discount
    ...(list.some((row) => row.discount && row.discount !== "0%")
      ? [{ title: "Discount%", dataIndex: "discount" }]
      : []),
    // Only show VAT column if at least one row has vat
    ...(showTax && list.some((row) => row.vat && row.vat !== "0%")
      ? [{ title: "VAT%", dataIndex: "vat" }]
      : []),
    { title: "Qty", dataIndex: "qty" },
    { title: "Total", dataIndex: "total" },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(`/service/quick-service-voucher/${record.id}`)
            }
          />
          {isAdminUser && (
            <Tooltip title="Delete Service">
              <Popconfirm
                title="Are you sure you want to delete this service?"
                okText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={() => handleDelete(record)}
              >
                <Button type="link" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}

          <VoucherPdfActions
            url={`/print/service/${record.id}`}
            fileName={`Service_${record.vch_no || record.id}.pdf`}
          />
        </>
      ),
    },
  ].filter(Boolean);

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;

  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.master_qty += Number(row.master_qty || 0);
        acc.base_qty += Number(row.base_qty || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 },
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.subtotal += Number(row.subtotal || 0);
        acc.discount += Number(row.discount || 0);
        acc.vat += Number(row.vat || 0);
        acc.cgst += Number(row.cgst_amount || 0);
        acc.sgst += Number(row.sgst_amount || 0);
        acc.igst += Number(row.igst_amount || 0);
        acc.service += Number(row.service || 0);
        acc.total += Number(row.total || 0);
        acc.paid += Number(row.paid || 0);
        acc.due += Number(row.due || 0);
        return acc;
      },
      {
        subtotal: 0,
        discount: 0,
        vat: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        service: 0,
        total: 0,
        paid: 0,
        due: 0,
      },
    );
  }

  // -----------------------
  // Expandable sub-row for WITH ITEM
  // -----------------------
  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <div style={{ fontSize: "12px", color: "#555" }}>
            {record.unitDetails}
          </div>
        ),
        rowExpandable: (record) => !!record.unitDetails,
      }
      : undefined;

  return (
    <Table
      rowKey="id"
      data-testid="service-record-table"
      columns={
        filters.recordType === "with item" ? withItemColumns : summaryColumns
      }
      dataSource={list}
      loading={loading}
      pagination={printMode ? false : { pageSize: 10 }}
      bordered
      expandable={expandable}
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with item") {
          // 👇 Calculate grand totals for with item
          const grand = list.reduce(
            (acc, row) => {
              acc.masterQty += row.masterQty || 0;
              acc.baseQty += row.baseQty || 0;
              acc.total += Number(row.total || 0);
              return acc;
            },
            { masterQty: 0, baseQty: 0, total: 0 },
          );

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={11}>
                <strong>
                  Grand Total : Master U. Qty = {grand.masterQty}, Base U. Qty ={" "}
                  {grand.baseQty}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11}>
                <strong>{grand.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={12}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        } else {
          // keep your existing "without item" summary
          const taxColOffset = (showTax ? 1 : 0) + (showGstColumns ? 3 : 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                {totals.subtotal.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                {totals.discount.toFixed(2)}
              </Table.Summary.Cell>
              {showTax && (
                <Table.Summary.Cell index={8}>
                  {totals.vat.toFixed(2)}
                </Table.Summary.Cell>
              )}

              {showGstColumns && (
                <Table.Summary.Cell index={9}>
                  {numberFormatTax(totals.cgst || 0)}
                </Table.Summary.Cell>
              )}
              {showGstColumns && (
                <Table.Summary.Cell index={10}>
                  {numberFormatTax(totals.sgst || 0)}
                </Table.Summary.Cell>
              )}
              {showGstColumns && (
                <Table.Summary.Cell index={11}>
                  {numberFormatTax(totals.igst || 0)}
                </Table.Summary.Cell>
              )}

              <Table.Summary.Cell index={8 + taxColOffset}>
                {totals.service.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9 + taxColOffset}>
                {totals.total.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10 + taxColOffset}>
                {totals.paid.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11 + taxColOffset}>
                {totals.due.toFixed(2)}
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={12 + taxColOffset}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default ServiceRecordTable;
